import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authService } from '../services/authService';
import { User, LoginCredentials } from '../types';
import { authService as authApi } from '../services/authService';

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginAdmin: (credentials: LoginCredentials) => Promise<void>;
  loginClient: (credentials: LoginCredentials) => Promise<void>;
  loginModerator: (credentials: LoginCredentials) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      const storedUser = authService.getUser();
      const token = authService.getToken();
      
      if (storedUser && token) {
        // Verify token is still valid by making a test request
        try {
          // Set user immediately for better UX
          setUser(storedUser);
          // Token will be validated on first API call
        } catch (error) {
          // If token is invalid, clear auth data
          authService.logout();
          setUser(null);
        }
      }
      setIsLoading(false);
    };
    
    checkAuth();
  }, []);

  const loginAdmin = async (credentials: LoginCredentials) => {
    try {
      const response = await authApi.loginAdmin(credentials);
      if (response.success && response.data) {
        const { access_token, user: userData } = response.data;
        // Store token and user data
        authService.setAuthData(access_token, userData);
        setUser(userData);
        
        // Verify token was stored
        const storedToken = localStorage.getItem('access_token');
        if (!storedToken || storedToken !== access_token) {
          console.error('Token storage verification failed');
          throw new Error('Failed to store authentication token');
        }
        
        // Set a flag to prevent immediate redirects after login
        sessionStorage.setItem('just_logged_in', 'true');
        setTimeout(() => {
          sessionStorage.removeItem('just_logged_in');
        }, 5000); // Increase to 5 seconds to give more time
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  };

  const loginClient = async (credentials: LoginCredentials) => {
    try {
      const response = await authApi.loginClient(credentials);
      if (response.success && response.data) {
        const { access_token, user: userData } = response.data;
        // Store token and user data
        authService.setAuthData(access_token, userData);
        setUser(userData);
        
        // Verify token was stored
        const storedToken = localStorage.getItem('access_token');
        if (!storedToken || storedToken !== access_token) {
          console.error('Token storage verification failed');
          throw new Error('Failed to store authentication token');
        }
        
        // Set a flag to prevent immediate redirects after login
        sessionStorage.setItem('just_logged_in', 'true');
        setTimeout(() => {
          sessionStorage.removeItem('just_logged_in');
        }, 5000); // Increase to 5 seconds to give more time
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  };

  const loginModerator = async (credentials: LoginCredentials) => {
    try {
      const response = await authApi.loginModerator(credentials);
      if (response.success && response.data) {
        const { access_token, user: userData } = response.data;
        // Store token and user data
        authService.setAuthData(access_token, userData);
        setUser(userData);
        
        // Verify token was stored
        const storedToken = localStorage.getItem('access_token');
        if (!storedToken || storedToken !== access_token) {
          console.error('Token storage verification failed');
          throw new Error('Failed to store authentication token');
        }
        
        // Set a flag to prevent immediate redirects after login
        sessionStorage.setItem('just_logged_in', 'true');
        setTimeout(() => {
          sessionStorage.removeItem('just_logged_in');
        }, 5000); // Increase to 5 seconds to give more time
      } else {
        throw new Error(response.message || 'Login failed');
      }
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Login failed');
    }
  };

  const logout = () => {
    authService.logout();
    setUser(null);
  };

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    loginAdmin,
    loginClient,
    loginModerator,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

